import { Input as UIKitInput } from "@credit-acceptance/uikit";
import type { ComponentProps } from "react";

/**
 * `ref` is dropped: uikit's TextArea exposes no ref hook, so there is nothing to
 * forward it to. Callers spread `{...field}` from React Hook Form, which only
 * uses the ref to focus the first invalid control on a failed submit.
 */
type TextareaProperties = Omit<ComponentProps<"textarea">, "ref" | "defaultValue"> & {
  defaultValue?: string;
};

/** Drop-in replacement for the shadcn `ui/textarea`, rendering uikit's TextArea. */
function Textarea({ className, value, onChange, ...properties }: TextareaProperties) {
  const uikitProperties = {
    kind: "textarea",
    size: "small",
    className,
    // Sets uikit's `data-invalid` styling hook; the DOM attribute is passed
    // through unchanged from `properties`.
    isError: properties["aria-invalid"] === true || properties["aria-invalid"] === "true",
    value: value === undefined ? undefined : String(value),
    onChange: (_next: string, _previous: string | undefined, event?: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (event) {
        onChange?.(event);
      }
    },
    ...properties,
    // See the note in ./input.tsx — uikit's props come from an older
    // @types/react, so the DOM attribute types need reconciling.
  } as ComponentProps<typeof UIKitInput>;

  return <UIKitInput {...uikitProperties} />;
}

export { Textarea };
